'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import { useRouter } from 'next/navigation'
import { API_BASE_URL, API_ENDPOINTS, PAYMENTS_ENABLED, TF_ENABLE_COMBO_PLAN } from '@/lib/config'
import { getAuthToken, authenticatedFetch } from '@/lib/auth'
import { filterComboPacks, fetchProductPacks, isComboPack, type ProductPack } from '@/lib/productPacks'
import { useLandingLogin } from '@/app/landing/LandingLoginProvider'
import { setPostLoginRedirect } from '@/lib/postLoginRedirect'
import { landingCtaClick } from '@/lib/ga'
import cardsConfig from '@/app/landing/components/pricingCardsConfig.json'
import { buildCheckoutAttribution } from '@/lib/analytics/checkoutAttribution'
import { analytics } from '@/lib/analytics'
import { navigateToProductCheckout, type CheckoutPaymentPlanType } from '@/lib/checkoutNavigation'

/**
 * Simple interview packs pay through /checkout, like the mock-interview, JD and system-design
 * entries do. The API requires billing details (phone, address, city, state, pincode) on
 * /payments/start and rejects the request with 400 "Phone number is required." when they are
 * missing — which is exactly what the in-page modal sent. The checkout page collects them.
 */
// Where a paid pack continues. Checkout arms a per-product success flag and redirects to return_to;
// only the product's own entry page consumes that flag and actually starts the session, so coming back
// to the campaign landing leaves the visitor with nothing to do after paying.
const POST_PAYMENT_DESTINATION: Partial<Record<CheckoutPaymentPlanType, string>> = {
  skill_assessment: '/mock-interview',
  system_design: '/system-design-interview',
  // JDInterviewFlow (success modal -> "Continue interview" -> JD link modal) is mounted on the
  // dashboard only, so that is where a JD purchase has to return.
  jd_prep: '/dashboard',
}

function goToCheckout(
  router: { push: (url: string) => void },
  pack: ProductPack,
  returnTo: string,
): void {
  const planType = pack.payment_plan_type as CheckoutPaymentPlanType
  navigateToProductCheckout(router, {
    productId: pack.product_id,
    paymentPlanType: planType as Exclude<CheckoutPaymentPlanType, 'practice_question'>,
    // Display price: the card and the breakdown said 199, so checkout must not open on 200.
    productPrice: pack.display_price ?? pack.price,
    returnTo: POST_PAYMENT_DESTINATION[planType] ?? returnTo,
  })
}

const PENDING_PACK_KEY = 'pricing_pending_pack'
const COMBO_CREDITS_KEY = 'combo_checkout_credits'

type PendingPack = {
  pack: ProductPack
  analyticsSource?: string
}

export type ComboCardConfigKey = keyof typeof cardsConfig

function trackDussehraCheckout(pack: ProductPack, analyticsSource?: string): void {
  if (analyticsSource !== 'dussehra_offer') return
  analytics.track('dussehra_checkout_started', { plan: pack.payment_plan_type })
}

function buildComboCheckoutUrl(pack: ProductPack): string {
  const params = new URLSearchParams({
    product_id: pack.product_id,
    payment_plan_type: pack.payment_plan_type,
    // Checkout / PayU still use canonical INR products.price — never display_price.
    product_price: String(pack.price),
    return_to: '/dashboard',
  })
  if (pack.product_name) params.set('product_name', pack.product_name)
  return `/checkout?${params.toString()}`
}

function storeComboCredits(pack: ProductPack): void {
  try {
    const credits = pack.credit_breakdown
      .filter((c) => c.credits > 0)
      .map((c) => ({ name: c.name, credits: c.credits }))
    sessionStorage.setItem(COMBO_CREDITS_KEY, JSON.stringify(credits))
  } catch {
    // ignore
  }
}

// ── Pure helpers (shared by every combo-plans presentation) ──────────────────

export function getPackConfigKey(pack: ProductPack): ComboCardConfigKey {
  const activeCount = pack.credit_breakdown.filter((c) => c.credits > 0).length
  if (activeCount >= 3) return 'combo_advanced'
  if (activeCount === 2) return 'combo'
  const hasMock = pack.credit_breakdown.some(
    (c) => c.credits > 0 && c.name.toLowerCase().includes('mock'),
  )
  return hasMock ? 'mock' : 'default'
}

/** Full list of bullet points for a card (API credits + config extras). */
export function getComboFeatures(pack: ProductPack): string[] {
  const active = pack.credit_breakdown.filter((c) => c.credits > 0)
  const creditLines = active.map((c) => `${c.credits} ${c.name}`)
  const { extraFeatures } = cardsConfig[getPackConfigKey(pack)]
  return [...creditLines, ...extraFeatures]
}

/** Place the advanced combo pack in the center; other packs on either side. */
export function sortPacksComboCenter(packs: ProductPack[]): ProductPack[] {
  const advancedIdx = packs.findIndex((p) => getPackConfigKey(p) === 'combo_advanced')
  if (advancedIdx === -1) {
    const nonCombos = packs.filter((p) => !isComboPack(p))
    const combos = packs.filter((p) => isComboPack(p))
    const mid = Math.floor(nonCombos.length / 2)
    return [...nonCombos.slice(0, mid), ...combos, ...nonCombos.slice(mid)]
  }
  const advanced = packs[advancedIdx]
  const rest = packs.filter((_, i) => i !== advancedIdx)
  const mid = Math.floor(rest.length / 2)
  return [...rest.slice(0, mid), advanced, ...rest.slice(mid)]
}

// ── Stateful controller (fetch, buy flow, payment popup + polling, modals) ───

export function useComboPricing() {
  const router = useRouter()
  const { openLoginModal } = useLandingLogin()
  const [packs, setPacks] = useState<ProductPack[]>([])
  const [ready, setReady] = useState(false)
  const [selectedPack, setSelectedPack] = useState<ProductPack | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [showPendingModal, setShowPendingModal] = useState(false)
  const paymentWindowRef = useRef<Window | null>(null)
  // Return path for the checkout hop, captured when the pricing modal is opened.
  const checkoutReturnRef = useRef<string>('/dashboard')
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const fetchPacks = useCallback(async () => {
    try {
      const data = await fetchProductPacks()
      setPacks(filterComboPacks(data))
    } catch {
      // Silently fail — section won't render
    } finally {
      setReady(true)
    }
  }, [])

  useEffect(() => {
    void fetchPacks()
  }, [fetchPacks])

  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current)
    }
  }, [])

  // Listen for postMessage from /payment/success popup — shows success immediately
  // without waiting for the poll cycle to detect window.closed.
  useEffect(() => {
    function handleMessage(event: MessageEvent) {
      if (event.origin !== window.location.origin) return
      if (event.data?.type === 'payment-success' && event.data?.paymentSuccess === true) {
        if (pollRef.current) {
          clearInterval(pollRef.current)
          pollRef.current = null
        }
        setIsModalOpen(false)
        setIsProcessing(false)
        setShowSuccessModal(true)
      }
      if (event.data?.type === 'payment-pending') {
        if (pollRef.current) {
          clearInterval(pollRef.current)
          pollRef.current = null
        }
        setIsModalOpen(false)
        setIsProcessing(false)
        setShowPendingModal(true)
      }
    }
    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [])

  // After login → restore pending pack. Combo → checkout page; others → modal.
  useEffect(() => {
    if (!ready) return
    const token = getAuthToken()
    if (!token) return
    const raw = sessionStorage.getItem(PENDING_PACK_KEY)
    if (!raw) return
    try {
      const parsed = JSON.parse(raw) as ProductPack | PendingPack
      const pack = 'pack' in parsed ? parsed.pack : parsed
      const analyticsSource = 'pack' in parsed ? parsed.analyticsSource : undefined
      sessionStorage.removeItem(PENDING_PACK_KEY)
      if (isComboPack(pack)) {
        storeComboCredits(pack)
        trackDussehraCheckout(pack, analyticsSource)
        router.push(buildComboCheckoutUrl(pack))
        return
      }
      checkoutReturnRef.current = '/dashboard'
      setSelectedPack(pack)
      setIsModalOpen(true)
    } catch {
      sessionStorage.removeItem(PENDING_PACK_KEY)
    }
  }, [ready, router])

  // Polls only for window-close. We do NOT call USER_ACCESS here because combo
  // packs have payment_plan_type='interview_combo' which that endpoint rejects
  // (it only accepts skill_assessment / jd_prep / system_design).
  // Credit creation is handled server-side by the PayU callback — closing the
  // window is the right signal to surface success to the user.
  const startPolling = useCallback(() => {
    if (pollRef.current) clearInterval(pollRef.current)
    let count = 0
    const MAX = 600

    pollRef.current = setInterval(() => {
      count++
      const win = paymentWindowRef.current

      if (win?.closed) {
        clearInterval(pollRef.current!)
        pollRef.current = null
        // Short delay so the PayU callback has time to reach the server
        setTimeout(() => {
          setIsModalOpen(false)
          setIsProcessing(false)
          setShowSuccessModal(true)
        }, 1500)
        return
      }

      if (count >= MAX) {
        clearInterval(pollRef.current!)
        pollRef.current = null
        setIsProcessing(false)
        alert('Payment timeout. Please check your payment status.')
      }
    }, 500)
  }, [])

  const handleBuy = useCallback(
    (pack: ProductPack, postLoginPath = '/', analyticsSource?: string) => {
      if (!getAuthToken()) {
        landingCtaClick({ cta_surface: 'pricing', journey: 'pricing_pack' })
        const pending: PendingPack = { pack, analyticsSource }
        sessionStorage.setItem(PENDING_PACK_KEY, JSON.stringify(pending))
        setPostLoginRedirect(postLoginPath)
        openLoginModal({ loginEntry: 'pricing' })
        return
      }
      if (isComboPack(pack)) {
        storeComboCredits(pack)
        trackDussehraCheckout(pack, analyticsSource)
        router.push(buildComboCheckoutUrl(pack))
        return
      }
      checkoutReturnRef.current = postLoginPath
      setSelectedPack(pack)
      setIsModalOpen(true)
    },
    [openLoginModal, router],
  )

  const handleUnlock = useCallback(async () => {
    if (!selectedPack) return

    // Simple interview packs: the modal shows the GST breakdown, then checkout collects the billing
    // details /payments/start requires. POSTing from here is what returned 400 "Phone number
    // is required." because this surface has no phone/address fields.
    if (!isComboPack(selectedPack)) {
      setIsModalOpen(false)
      goToCheckout(router, selectedPack, checkoutReturnRef.current)
      return
    }

    setIsProcessing(true)
    try {
      const res = await authenticatedFetch(`${API_BASE_URL}${API_ENDPOINTS.PAYMENT_START}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          product_id: selectedPack.product_id,
          payment_plan_type: selectedPack.payment_plan_type,
          // Carried to the purchase event, which is emitted server-side after the
          // browser is gone. Without it the sale attributes to no campaign.
          analytics: buildCheckoutAttribution(),
        }),
      })

      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error(
          (typeof err.message === 'string' && err.message) ||
            (typeof err.detail === 'string' && err.detail) ||
            'Failed to initiate payment',
        )
      }

      const htmlContent = await res.text()
      const popupWidth = 800
      const popupHeight = 600
      const popupLeft = Math.round((window.screen.width - popupWidth) / 2)
      const popupTop = Math.round((window.screen.height - popupHeight) / 2)
      const win = window.open(
        '',
        'payment',
        `width=${popupWidth},height=${popupHeight},left=${popupLeft},top=${popupTop},scrollbars=yes,resizable=yes`,
      )
      if (!win) throw new Error('Popup blocked. Please allow popups for this site.')
      const blob = new Blob([htmlContent], { type: 'text/html' })
      const blobUrl = URL.createObjectURL(blob)
      win.location.replace(blobUrl)
      setTimeout(() => URL.revokeObjectURL(blobUrl), 10_000)
      paymentWindowRef.current = win
      startPolling()
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Failed to start payment')
      setIsProcessing(false)
    }
  }, [selectedPack, startPolling, router])

  const handleCancel = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current)
      pollRef.current = null
    }
    try {
      paymentWindowRef.current?.close()
    } catch {
      // ignore
    }
    setIsModalOpen(false)
    setIsProcessing(false)
    setSelectedPack(null)
  }, [])

  const visible = PAYMENTS_ENABLED && TF_ENABLE_COMBO_PLAN && ready && packs.length > 0

  return {
    visible,
    packs,
    ready,
    selectedPack,
    isModalOpen,
    isProcessing,
    showSuccessModal,
    setShowSuccessModal,
    showPendingModal,
    setShowPendingModal,
    handleBuy,
    handleUnlock,
    handleCancel,
  }
}
